import React from 'react';
import { useTranslation } from 'localization';
import { PublisherStatus } from 'types/publisher';
import './PublisherStatusCalloutMolecule.scss';

export interface PublisherStatusCalloutMoleculeProps {
  status: PublisherStatus;
  reviewerNote?: string | null;
  /** ISO date string of when the publisher was approved. Shown in the published callout. */
  reviewedAt?: string | null;
  className?: string;
}

const STATUS_META: Record<PublisherStatus, { icon: string; variant: string; labelKey: string; descKey: string }> = {
  draft: {
    icon: 'pi-file-edit',
    variant: 'neutral',
    labelKey: 'marketplace.publishers.status.draft',
    descKey: 'marketplace.publishers.statusCallout.draftDesc',
  },
  pending_review: {
    icon: 'pi-clock',
    variant: 'warning',
    labelKey: 'marketplace.publishers.status.pendingReview',
    descKey: 'marketplace.publishers.statusCallout.pendingDesc',
  },
  published: {
    icon: 'pi-check-circle',
    variant: 'success',
    labelKey: 'marketplace.publishers.status.published',
    descKey: 'marketplace.publishers.statusCallout.publishedDesc',
  },
  rejected: {
    icon: 'pi-times-circle',
    variant: 'danger',
    labelKey: 'marketplace.publishers.status.rejected',
    descKey: 'marketplace.publishers.statusCallout.rejectedDesc',
  },
};

const PublisherStatusCalloutMolecule: React.FC<PublisherStatusCalloutMoleculeProps> = ({
  status,
  reviewerNote,
  reviewedAt,
  className = '',
}) => {
  const { t } = useTranslation();
  const meta = STATUS_META[status] ?? STATUS_META.draft;

  const classes = [
    'marketplace-publisher-status-callout',
    `marketplace-publisher-status-callout--${meta.variant}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Format the approval date for the published callout — e.g. "Jan 4, 2026".
  const approvedDateLabel = reviewedAt
    ? new Date(reviewedAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className={classes} role="status">
      <div className="marketplace-publisher-status-callout__header">
        <i className={`pi ${meta.icon} marketplace-publisher-status-callout__icon`} aria-hidden="true" />
        {status === 'published' ? (
          // Rich single-line format for published state: label + context inline.
          <span className="marketplace-publisher-status-callout__label">
            <strong>{t(meta.labelKey)}.</strong>{' '}
            {t('marketplace.publishers.statusCallout.publishedDesc')}
            {approvedDateLabel && (
              <>{' '}{t('marketplace.publishers.statusCallout.approvedOn', { date: approvedDateLabel })}</>
            )}
            {' '}{t('marketplace.publishers.statusCallout.publishedRereviewHint')}
          </span>
        ) : (
          <span className="marketplace-publisher-status-callout__label">{t(meta.labelKey)}</span>
        )}
      </div>
      {status !== 'published' && (
        <p className="marketplace-publisher-status-callout__desc">{t(meta.descKey)}</p>
      )}
      {status === 'rejected' && reviewerNote && (
        <blockquote className="marketplace-publisher-status-callout__note">
          {reviewerNote}
        </blockquote>
      )}
    </div>
  );
};

PublisherStatusCalloutMolecule.displayName = 'PublisherStatusCalloutMolecule';

export default PublisherStatusCalloutMolecule;
